/**
 * API Logs Page
 * View and debug third-party API calls (Verso, Core API, etc.)
 */

import { useState, useEffect, useCallback } from 'react';
import { Button, Card, Badge, Input } from '../components/ui';
import { JsonViewer } from '../components/ui/JsonViewer';
import { 
  RefreshCw, 
  Search, 
  ChevronDown, 
  ChevronRight,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Trash2,
  Filter,
  Code,
  Copy,
  Check,
  Maximize2,
  X,
} from 'lucide-react';
import { getLogs, getLogTypes, deleteLogs, getEtaUsage, type LogEntry, type LogTypeCount, type EtaUsage } from '../services/logs.api';
import { cn } from '../utils/cn';
import { useCan } from '../contexts/useAuth';

export default function ApiLogs() {
  const can = useCan();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [logTypes, setLogTypes] = useState<LogTypeCount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [limit, setLimit] = useState(50);
  
  // Pagination
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // Expanded log details
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  
  // Fullscreen modal for viewing log details
  const [fullscreenLog, setFullscreenLog] = useState<LogEntry | null>(null);

  // ETA provider month-to-date usage vs budget (cost control)
  const [etaUsage, setEtaUsage] = useState<EtaUsage | null>(null);

  // Fetch logs
  const fetchLogs = useCallback(async (append = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setOffset(0);
    }
    setError(null);
    getEtaUsage().then(setEtaUsage).catch(() => undefined);
    try {
      const currentOffset = append ? offset : 0;
      const [logsData, typesData] = await Promise.all([
        getLogs({
          type: selectedType || undefined,
          level: selectedLevel || undefined,
          action: searchQuery || undefined,
          limit,
          offset: currentOffset,
        }),
        getLogTypes(),
      ]);
      
      if (append) {
        setLogs(prev => [...prev, ...logsData.logs]);
      } else {
        setLogs(logsData.logs);
      }
      
      setLogTypes(typesData);
      setTotal(logsData.pagination.total);
      setHasMore(logsData.pagination.hasMore);
      setOffset(currentOffset + logsData.logs.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch logs');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [selectedType, selectedLevel, searchQuery, limit, offset]);

  useEffect(() => {
    fetchLogs(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, selectedLevel, searchQuery, limit]);

  // Handle delete logs
  const handleDeleteLogs = async () => {
    if (!window.confirm('Are you sure you want to delete all logs of this type? This cannot be undone.')) {
      return;
    }
    try {
      await deleteLogs({ type: selectedType || undefined });
      fetchLogs(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete logs');
    }
  };

  // Load more logs
  const handleLoadMore = () => {
    fetchLogs(true);
  };

  // Copy JSON to clipboard
  const copyToClipboard = async (data: unknown, id: string) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Format timestamp
  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'Asia/Baghdad',
    });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
    });
  };

  // Get level badge
  const getLevelBadge = (level: string) => {
    switch (level) {
      case 'error':
        return <Badge variant="danger" size="sm">Error</Badge>;
      case 'warn':
        return <Badge variant="warning" size="sm">Warn</Badge>;
      case 'info':
        return <Badge variant="info" size="sm">Info</Badge>;
      case 'debug':
        return <Badge variant="default" size="sm">Debug</Badge>;
      default:
        return <Badge variant="default" size="sm">{level}</Badge>;
    }
  };

  // Get action icon
  const getActionIcon = (action: string, level: string) => {
    if (level === 'error') {
      return <XCircle className="h-4 w-4 text-red-500" />;
    }
    if (level === 'warn') {
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    }
    if (action.includes('request')) {
      return <ChevronRight className="h-4 w-4 text-blue-500" />;
    }
    if (action.includes('response')) {
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
    return <Code className="h-4 w-4 text-gray-400" />;
  };

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">API Logs</h1>
            <p className="text-gray-500 text-sm">
              Debug third-party API calls (Verso, Core API, etc.)
            </p>
          </div>
          <div className="flex items-center gap-2">
            {etaUsage && (
              <div
                className={cn(
                  'flex items-center gap-3 px-3 py-1.5 rounded-lg border text-xs',
                  etaUsage.blocked
                    ? 'border-red-300 bg-red-50'
                    : etaUsage.warn
                      ? 'border-yellow-300 bg-yellow-50'
                      : 'border-gray-200 bg-gray-50'
                )}
                title={`ETA provider: ${etaUsage.provider}. Calls pause automatically at 100% of the monthly budget (free tier: ${etaUsage.free_tier_limit.toLocaleString()}/month).`}
              >
                <div>
                  <div className="font-medium text-gray-700">
                    ETA calls · {etaUsage.month}
                  </div>
                  <div className="w-36 h-1.5 bg-gray-200 rounded-full mt-1">
                    <div
                      className={cn(
                        'h-1.5 rounded-full',
                        etaUsage.blocked ? 'bg-red-500' : etaUsage.warn ? 'bg-yellow-500' : 'bg-green-500'
                      )}
                      style={{ width: `${Math.min(100, etaUsage.percent)}%` }}
                    />
                  </div>
                </div>
                <div className="text-gray-600 whitespace-nowrap">
                  {etaUsage.calls.toLocaleString()} / {etaUsage.budget.toLocaleString()}
                  <span className="text-gray-500"> ({etaUsage.percent}%)</span>
                </div>
                {etaUsage.blocked ? (
                  <Badge variant="danger" size="sm">paused</Badge>
                ) : etaUsage.warn ? (
                  <Badge variant="warning" size="sm">near budget</Badge>
                ) : null}
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              leftIcon={<RefreshCw className="h-4 w-4" />}
              onClick={() => fetchLogs(false)}
              loading={isLoading}
            >
              Refresh
            </Button>
            {can('logs.delete') && (
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Trash2 className="h-4 w-4" />}
                onClick={handleDeleteLogs}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                Clear Logs
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="verso">Verso API</option>
              <option value="core_api">Core API</option>
              <option value="google_eta">Google ETA</option>
              <option value="mapbox_eta">Mapbox ETA</option>
              <option value="error">Errors</option>
            </select>
          </div>

          {/* Level Filter */}
          <select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Levels</option>
            <option value="debug">Debug</option>
            <option value="info">Info</option>
            <option value="warn">Warning</option>
            <option value="error">Error</option>
          </select>

          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search action..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              size="sm"
            />
          </div>

          {/* Limit */}
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="25">25 logs</option>
            <option value="50">50 logs</option>
            <option value="100">100 logs</option>
            <option value="200">200 logs</option>
          </select>

          {/* Type counts */}
          <div className="flex items-center gap-2 ml-auto">
            {logTypes.slice(0, 4).map((t) => (
              <button
                key={t.type}
                onClick={() => setSelectedType(t.type)}
                className={cn(
                  'px-2 py-1 rounded text-xs font-medium transition-colors',
                  selectedType === t.type
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {t.type}: {t.count}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 border-b border-red-200 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          <p className="text-red-800">{error}</p>
          <Button variant="outline" size="sm" onClick={() => fetchLogs(false)}>
            Retry
          </Button>
        </div>
      )}

      {/* Logs List */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : logs.length === 0 ? (
          <Card className="p-8 text-center">
            <Code className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No logs found</h3>
            <p className="text-gray-500 text-sm">
              API calls will appear here when you generate routes or make other API requests.
            </p>
          </Card>
        ) : (
          <div className="space-y-2">
            {logs.map((log) => {
              const isExpanded = expandedLogId === log._id;
              
              return (
                <Card
                  key={log._id}
                  padding="none"
                  className={cn(
                    'transition-all',
                    isExpanded && 'ring-2 ring-blue-500'
                  )}
                >
                  {/* Log Header */}
                  <button
                    onClick={() => setExpandedLogId(isExpanded ? null : log._id)}
                    className="w-full p-3 flex items-center gap-3 text-left hover:bg-gray-50"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    )}
                    
                    {getActionIcon(log.action, log.level)}
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium text-gray-900 truncate">
                          {log.action}
                        </span>
                        {getLevelBadge(log.level)}
                        <Badge variant="default" size="sm">{log.type}</Badge>
                      </div>
                    </div>
                    
                    {log.duration_ms && (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="h-3 w-3" />
                        {log.duration_ms}ms
                      </div>
                    )}
                    
                    <div className="text-xs text-gray-500 flex-shrink-0">
                      {formatDate(log.created_at)} {formatTime(log.created_at)}
                    </div>
                  </button>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                      {/* Fullscreen button */}
                      <div className="flex justify-end mb-3">
                        <button
                          onClick={() => setFullscreenLog(log)}
                          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors"
                        >
                          <Maximize2 className="h-4 w-4" />
                          View Fullscreen
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Request/Payload */}
                        {log.payload && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-medium text-gray-700">Request / Payload</h4>
                              <button
                                onClick={() => copyToClipboard(log.payload, `payload-${log._id}`)}
                                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded"
                              >
                                {copiedId === `payload-${log._id}` ? (
                                  <>
                                    <Check className="h-3 w-3 text-green-500" />
                                    Copied!
                                  </>
                                ) : (
                                  <>
                                    <Copy className="h-3 w-3" />
                                    Copy
                                  </>
                                )}
                              </button>
                            </div>
                            <div className="bg-gray-900 p-3 rounded-lg overflow-auto max-h-96">
                              <JsonViewer data={log.payload} colorScheme="green" />
                            </div>
                          </div>
                        )}

                        {/* Response */}
                        {log.response && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-medium text-gray-700">Response</h4>
                              <button
                                onClick={() => copyToClipboard(log.response, `response-${log._id}`)}
                                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded"
                              >
                                {copiedId === `response-${log._id}` ? (
                                  <>
                                    <Check className="h-3 w-3 text-green-500" />
                                    Copied!
                                  </>
                                ) : (
                                  <>
                                    <Copy className="h-3 w-3" />
                                    Copy
                                  </>
                                )}
                              </button>
                            </div>
                            <div className="bg-gray-900 p-3 rounded-lg overflow-auto max-h-96">
                              <JsonViewer data={log.response} colorScheme="blue" />
                            </div>
                          </div>
                        )}

                        {/* Error */}
                        {log.error && (
                          <div className="lg:col-span-2">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-medium text-red-700">Error</h4>
                            </div>
                            <div className="bg-red-900 p-3 rounded-lg overflow-auto max-h-96">
                              <JsonViewer data={log.error} colorScheme="red" />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Metadata */}
                      <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-4 text-xs text-gray-500">
                        <span>ID: {log._id}</span>
                        {log.route_id && <span>Route: {log.route_id}</span>}
                        {log.driver_uid && <span>Driver: {log.driver_uid}</span>}
                        {log.task_uid && <span>Task: {log.task_uid}</span>}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
            
            {/* Load More Button */}
            {hasMore && (
              <div className="flex justify-center pt-4">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  loading={isLoadingMore}
                  className="min-w-[200px]"
                >
                  {isLoadingMore ? 'Loading...' : `Load More (${logs.length} of ${total})`}
                </Button>
              </div>
            )}
            
            {/* Pagination Info */}
            {!hasMore && logs.length > 0 && (
              <div className="text-center pt-4 text-sm text-gray-500">
                Showing all {total} logs
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fullscreen Modal */}
      {fullscreenLog && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full h-full max-w-[95vw] max-h-[95vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-3">
                <Code className="h-5 w-5 text-gray-500" />
                <div>
                  <h2 className="font-mono text-lg font-semibold text-gray-900">
                    {fullscreenLog.action}
                  </h2>
                  <div className="flex items-center gap-2 mt-1">
                    {getLevelBadge(fullscreenLog.level)}
                    <Badge variant="default" size="sm">{fullscreenLog.type}</Badge>
                    {fullscreenLog.duration_ms && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {fullscreenLog.duration_ms}ms
                      </span>
                    )}
                    <span className="text-xs text-gray-500">
                      {formatDate(fullscreenLog.created_at)} {formatTime(fullscreenLog.created_at)}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setFullscreenLog(null)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="h-6 w-6 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-4">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 h-full">
                {/* Request/Payload */}
                {fullscreenLog.payload && (
                  <div className="flex flex-col min-h-0">
                    <div className="flex items-center justify-between mb-2 flex-shrink-0">
                      <h3 className="text-sm font-semibold text-gray-700">Request / Payload</h3>
                      <button
                        onClick={() => copyToClipboard(fullscreenLog.payload, `fs-payload-${fullscreenLog._id}`)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded"
                      >
                        {copiedId === `fs-payload-${fullscreenLog._id}` ? (
                          <>
                            <Check className="h-4 w-4 text-green-500" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                    <div className="flex-1 bg-gray-900 p-4 rounded-lg overflow-auto min-h-[300px]">
                      <JsonViewer data={fullscreenLog.payload} colorScheme="green" />
                    </div>
                  </div>
                )}

                {/* Response */}
                {fullscreenLog.response && (
                  <div className="flex flex-col min-h-0">
                    <div className="flex items-center justify-between mb-2 flex-shrink-0">
                      <h3 className="text-sm font-semibold text-gray-700">Response</h3>
                      <button
                        onClick={() => copyToClipboard(fullscreenLog.response, `fs-response-${fullscreenLog._id}`)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded"
                      >
                        {copiedId === `fs-response-${fullscreenLog._id}` ? (
                          <>
                            <Check className="h-4 w-4 text-green-500" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                    <div className="flex-1 bg-gray-900 p-4 rounded-lg overflow-auto min-h-[300px]">
                      <JsonViewer data={fullscreenLog.response} colorScheme="blue" />
                    </div>
                  </div>
                )}

                {/* Error */}
                {fullscreenLog.error && (
                  <div className="xl:col-span-2 flex flex-col min-h-0">
                    <h3 className="text-sm font-semibold text-red-700 mb-2 flex-shrink-0">Error</h3>
                    <div className="flex-1 bg-red-900 p-4 rounded-lg overflow-auto min-h-[200px]">
                      <JsonViewer data={fullscreenLog.error} colorScheme="red" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <div className="text-xs text-gray-500 flex items-center gap-4">
                <span>ID: {fullscreenLog._id}</span>
                {fullscreenLog.route_id && <span>Route: {fullscreenLog.route_id}</span>}
                {fullscreenLog.driver_uid && <span>Driver: {fullscreenLog.driver_uid}</span>}
              </div>
              <button
                onClick={() => setFullscreenLog(null)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
